import { useState } from 'react';
import type { PageMode } from '../admin/newContentManagement/newContentManagementEditorTypes';
import type { FundManagementToolbarState } from './SimplifiedFundUpdatesAdminPanel';

function isPreviewMode(mode: PageMode): boolean {
  return mode === 'preview-web' || mode === 'preview-app';
}

/**
 * The Fund Management toolbar's View mode. Preview Web and Preview App always show the
 * whole-page investor/app preview, whether or not a single update's editor happens to be open —
 * they always show the same thing (every update as currently published/saved), so which one you
 * land on shouldn't depend on an open update row. Edit and Library still drive that open update's
 * own editor when there is one, and fall back to the page-level view otherwise.
 *
 * `generation` changes every time a page-level preview is entered, so the preview reloads the
 * latest updates, drafts and investor data rather than showing what it fetched last time.
 */
export function usePageLevelPreviewMode(toolbarState: FundManagementToolbarState | null) {
  const [pageMode, setPageModeState] = useState<PageMode>('edit');
  const [generation, setGeneration] = useState(0);
  const editorSetPageMode = toolbarState?.setPageMode;

  const setPageMode = (mode: PageMode) => {
    if (mode !== 'edit' && mode !== pageMode) setGeneration((previous) => previous + 1);
    setPageModeState(mode);
  };

  if (isPreviewMode(pageMode)) {
    return { pageMode, setPageMode, isPageLevelPreview: true, generation };
  }

  if (editorSetPageMode) {
    return {
      pageMode: toolbarState?.pageMode ?? 'edit',
      setPageMode: (mode: PageMode) =>
        isPreviewMode(mode) ? setPageMode(mode) : editorSetPageMode(mode),
      isPageLevelPreview: false,
      generation,
    };
  }

  return {
    pageMode,
    setPageMode,
    isPageLevelPreview: pageMode !== 'edit',
    generation,
  };
}
